'use client';
import {
  ImportableDocumentGroup,
  ImportableDocumentItem,
  ImportableDocumentSection,
} from 'bff';
import { OrderRelationType } from 'database';
import { useCallback, useMemo } from 'react';
import { Button, Checkbox, InputDebounceSearch, Skeleton } from 'ui';
import { FilesDisplayer, FilesDisplayerItem } from './FilesDisplayer';

// order_document_import — selectable view of the record's documentation, built on
// the same FilesDisplayer as the order's documentation tab: the checkbox rides in
// its per-item `renderItemActions` slot. Files are shared BY REFERENCE.
const GROUP_TITLES: Record<ImportableDocumentGroup, string> = {
  claimant: 'Documentación del reclamante',
  tramitation: 'Documentación de tramitación',
  // A complaint has no claimant side, so its documentation is one flat list.
  all: 'Documentación de la denuncia',
};

const authorName = (uploadedBy: ImportableDocumentItem['uploaded_by']) => {
  if (!uploadedBy) return null;
  return (
    [uploadedBy.first_name, uploadedBy.last_name].filter(Boolean).join(' ') ||
    null
  );
};

const toDisplayerItem = (
  document: ImportableDocumentItem,
): FilesDisplayerItem => ({
  id: document.file_id,
  name: document.original_name,
  size: document.size,
  mimetype: document.mimetype,
  url: document.url,
  uploadedBy: authorName(document.uploaded_by),
  uploadedAt: document.created_at,
});

const Section = ({
  section,
  value,
  onToggle,
  onToggleAll,
  reachedMax,
}: {
  section: ImportableDocumentSection;
  value: string[];
  onToggle: (fileId: string) => void;
  onToggleAll: (fileIds: string[], select: boolean) => void;
  reachedMax: boolean;
}) => {
  // Memoized: the two callbacks below close over it and would otherwise be
  // rebuilt on every render.
  const byFileId = useMemo(
    () =>
      new Map(
        section.documents.map((document) => [document.file_id, document]),
      ),
    [section.documents],
  );
  const selectable = section.documents.filter((document) => !document.imported);
  const allSelected =
    selectable.length > 0 &&
    selectable.every((document) => value.includes(document.file_id));

  const handleToggleAll = useCallback(
    () =>
      onToggleAll(
        selectable.map((document) => document.file_id),
        !allSelected,
      ),
    [allSelected, onToggleAll, selectable],
  );

  const renderItemActions = useCallback(
    (file: FilesDisplayerItem) => {
      const document = byFileId.get(file.id);
      if (!document) return null;

      const checked = document.imported || value.includes(file.id);
      const disabled =
        document.imported || (reachedMax && !value.includes(file.id));

      return (
        <div className='flex items-center gap-1.5'>
          {/* {document.imported && (
            <span className='rounded bg-white/90 px-1 text-[11px] text-muted-foreground'>
              Ya adjunto
            </span>
          )} */}
          <Checkbox
            className='bg-white'
            aria-label={`Seleccionar ${document.original_name}`}
            checked={checked}
            disabled={disabled}
            onCheckedChange={() => onToggle(file.id)}
          />
        </div>
      );
    },
    [byFileId, onToggle, reachedMax, value],
  );

  const onItemClick = useCallback(
    (file: FilesDisplayerItem) => {
      const document = byFileId.get(file.id);
      // Clicking the card selects instead of previewing: the picker already runs
      // inside a dialog, and selecting is what the user came to do here.
      if (document && !document.imported) onToggle(file.id);
    },
    [byFileId, onToggle],
  );

  if (section.documents.length === 0) return null;

  return (
    <FilesDisplayer
      title={GROUP_TITLES[section.group]}
      files={section.documents.map(toDisplayerItem)}
      maxHeight='30rem'
      renderItemActions={renderItemActions}
      onItemClick={onItemClick}
      action={
        selectable.length > 0 && (
          <Button
            type='button'
            size='sm'
            variant='link'
            className='h-auto p-0 text-xs'
            onClick={handleToggleAll}
          >
            {allSelected ? 'Quitar selección' : 'Seleccionar todos'}
          </Button>
        )
      }
    />
  );
};

export const ImportOrderDocumentsPicker = ({
  relationType,
  sections,
  truncated,
  isLoading,
  value,
  onValueChange,
  onSearchChange,
  maxDocuments,
}: {
  relationType?: OrderRelationType;
  sections: ImportableDocumentSection[];
  truncated?: boolean;
  isLoading?: boolean;
  value: string[];
  onValueChange: (fileIds: string[]) => void;
  onSearchChange: (search: string) => void;
  maxDocuments: number;
}) => {
  const reachedMax = value.length >= maxDocuments;
  const recordLabel =
    relationType === 'complaint' ? 'la denuncia' : 'el reclamo';

  const onToggle = useCallback(
    (fileId: string) =>
      onValueChange(
        value.includes(fileId)
          ? value.filter((id) => id !== fileId)
          : value.length >= maxDocuments
          ? value
          : value.concat(fileId),
      ),
    [maxDocuments, onValueChange, value],
  );

  const onToggleAll = useCallback(
    (fileIds: string[], select: boolean) => {
      const next = select
        ? Array.from(new Set(value.concat(fileIds))).slice(0, maxDocuments)
        : value.filter((id) => !fileIds.includes(id));
      onValueChange(next);
    },
    [maxDocuments, onValueChange, value],
  );

  const isEmpty = sections.every((section) => section.documents.length === 0);

  return (
    <div className='flex flex-col gap-3'>
      {/* <p className='text-sm text-muted-foreground'>
        Seleccioná la documentación de {recordLabel} que querés compartir con el
        proveedor. Los archivos se comparten por referencia: no se copian ni se
        vuelven a subir, y el proveedor podrá verlos.
      </p> */}

      <InputDebounceSearch
        placeholder='Buscar documento...'
        onDebouncedChange={onSearchChange}
      />

      {isLoading ? (
        <div className='flex flex-col gap-2'>
          <Skeleton className='h-10 w-full' />
          <Skeleton className='h-10 w-full' />
          <Skeleton className='h-10 w-full' />
        </div>
      ) : isEmpty ? (
        <p className='py-4 text-center text-sm text-muted-foreground'>
          No hay documentación disponible en {recordLabel}.
        </p>
      ) : (
        <div className='flex max-h-[45vh] flex-col gap-4 overflow-y-auto pr-1'>
          {sections.map((section) => (
            <Section
              key={section.group}
              section={section}
              value={value}
              onToggle={onToggle}
              onToggleAll={onToggleAll}
              reachedMax={reachedMax}
            />
          ))}
        </div>
      )}

      {truncated && (
        <p className='text-xs text-muted-foreground'>
          Hay más documentos de los que se pueden mostrar. Refiná la búsqueda
          para encontrarlos.
        </p>
      )}

      <p className='text-xs text-muted-foreground'>
        {value.length} seleccionado{value.length === 1 ? '' : 's'}
        {reachedMax ? ` (máximo ${maxDocuments})` : ''}
      </p>
    </div>
  );
};
